import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "header" }
const _hoisted_2 = { class: "title d-flex flex-wrap" }
const _hoisted_3 = {
  key: 1,
  class: "subitem-status"
}
const _hoisted_4 = {
  key: 0,
  class: "check-content upload"
}
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "check-content" }

import Tile from "@/components/shared/Tile.vue";
import { useStore } from "vuex";
import { computed, reactive, toRefs } from "vue";
import type { Applicant } from "@/models/application";
import UppyUploader from "@/components/shared/UppyUploader.vue";
import InlineUppyUploader from "@/components/shared/InlineUppyUploader.vue";
import AddressMismatch from "@/components/checklist/address-mismatch/AddressMismatch.vue";
import ESign from "@/components/checklist/ESign.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Pill from "./shared/Pill.vue";
import StipulationText from "@/components/checklist/StipulationText.vue";
import useFormatter from "@/use/formatter";
import Membership from "@/components/checklist/Membership.vue";
import NameInput from "@/components/checklist/NameInput.vue";
import AddressInput from "@/components/checklist/AddressInput.vue";
import DateOfBirthInput from "@/components/checklist/DateOfBirthInput.vue";
import SocialSecurityNumberInput from "@/components/checklist/SocialSecurityNumberInput.vue";
import ConfirmPayoffRequestInput from "@/components/checklist/ConfirmPayoffRequestInput.vue";
import RefiUnderpaymentConsent from "@/components/checklist/RefiUnderpaymentConsent.vue";
import ServicerPayoffAddressInput from "@/components/checklist/ServicerPayoffAddressInput.vue";
import SelfCertificationDetailsInput from "@/components/checklist/SelfCertificationDetailsInput.vue";
import AutomatedIncomeVerificationConsent from "@/components/checklist/income-verification/AutomatedIncomeVerificationConsent.vue";
import AutomatedIncomeVerification from "@/components/checklist/income-verification/AutomatedIncomeVerification.vue";
import { useStipulation } from "@/composables/stipulations/useStipulation";
import type { Stipulation } from "@/models/opal";
import { useOidcStore } from "@/use/useOidcStore";
import {
  PayoffVerificationStipulationTypeIds,
  EsignStipulationTypeIds,
  RegulatoryStipulationTypeIds,
  ApplicantTypeIds,
  IncomeStipulationTypeIds,
  ProcessingCategoryIds,
  CreditStipulationTypeIds,
  MembershipStipulationTypeIds,
  IdentityStipulationTypeIds,
  StipulationStatusIds,
} from "@/config/constants";

interface Props {
  title: string;
  subitem: any;
  active: boolean;
  itemComplete: boolean;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ApplicationChecklistSubItem',
  props: {
    title: {},
    subitem: {},
    active: { type: Boolean },
    itemComplete: { type: Boolean }
  },
  emits: [
  "esignClicked",
  "handleUploadSuccess",
  "incomeSourcesUpdated",
  "addressMismatchStipUpdated",
],
  setup(__props: any, { emit: __emit }) {

const store = useStore();

const props = __props;

const { subitem } = toRefs(props);

const stipulation = computed<Stipulation>(() => props.subitem);

const { stipulationText, isComplete, isPending, assignedApplicant, isApplicantStip } =
  useStipulation(stipulation);
const { formatFullName } = useFormatter();
const { oidcUser, oidcIsAuthenticated } = useOidcStore();

const emit = __emit;

const displayName = computed<string>(() => {
  return isApplicantStip.value ? `${fullName.value}` : `${currentApplicantLoggedInFullName.value}`;
});

const iconConfiguration = computed<any | undefined>(() => {
  switch (stipulation.value.stipulationStatusId) {
    case StipulationStatusIds.OPEN:
      return {
        icon: ["fas", "circle-exclamation"],
        class: "icon incomplete",
      };

    case StipulationStatusIds.COMPLETED:
    case StipulationStatusIds.CU_OVERRIDE:
      return {
        icon: ["fas", "circle-check"],
        class: "icon complete",
      };

    case StipulationStatusIds.PENDING:
      return {
        icon: ["fas", "clock"],
        class: "icon yellow",
      };
    default:
      return undefined;
  }
});

const active = computed(() => props?.active);

const complete = computed(() => isComplete.value);

const pending = computed(() => isPending.value);

const applicant = computed(() =>
  store.state.appData.applicants.find((a: Applicant) => a.id === props?.subitem.applicantId),
);

const currentApplicant = computed(() =>
  store.state.appData.applicants.find((a: Applicant) => a.userId === oidcUser?.value?.sub),
);

const coApplicantTypeDisplayName = computed(() => store.getters["coApplicantTypeDisplayName"]);

const applicantTypeDisplayName = computed<string>(() => getApplicantTypeDisplayName() || "");

const files = reactive({} as any);

const fullName = computed(() =>
  oidcIsAuthenticated.value ? `${formatFullName(assignedApplicant.value)}'s` : "",
);

const currentApplicantLoggedInFullName = computed(() =>
  oidcIsAuthenticated.value ? `${formatFullName(currentApplicant.value)}'s` : "",
);

const formattedTitle = computed(() => getTitle());

async function handleUpload(event: any) {
  emit("handleUploadSuccess", event);
}

function handleUpdateConsentStipResponseSuccess() {
  store.dispatch("checklistModule/getSortedItemCompleteStatusByTitle", props.title);
}

function handleStipSuccess(updatedStip?: Stipulation) {
  if (updatedStip) {
    subitem.value.stipulationStatusId = updatedStip?.stipulationStatusId;
    subitem.value.stipulationStatusName = updatedStip?.stipulationStatusName;

    store.dispatch("checklistModule/updateStipVuexState", {
      stipulation: updatedStip,
      title: props.title,
    });

    store.dispatch("checklistModule/getSortedChecklistItemStatusByTitle", props.title);
  } else {
    subitem.value.stipulationStatusId = StipulationStatusIds.PENDING;
    subitem.value.stipulationStatusName = "Pending";

    store.dispatch("checklistModule/updateStipVuexState", {
      stipulation: subitem.value,
      title: props.title,
    });
    store.dispatch("checklistModule/getSortedChecklistItemStatusByTitle", props.title);
  }
}

function getTitle() {
  if (props.subitem?.processingCategoryName === "Income") {
    const re = /\b(19|20)\d{2}\b/;
    let result = props.subitem?.stipulationText.match(re);

    if (props.subitem.stipulationTypeId === IncomeStipulationTypeIds.IncomeVerification) {
      return props.subitem.stipulationText;
    }

    if (result?.length > 0) {
      return props?.subitem?.stipulationTypeDescription;
    } else {
      return props.title;
    }
  } else {
    return props?.subitem?.stipulationTypeDescription;
  }
}

function getApplicantTypeDisplayName() {
  return assignedApplicant.value?.applicantTypeId === ApplicantTypeIds.PRIMARY_APPLICANT
    ? "Borrower"
    : coApplicantTypeDisplayName.value;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["checklist-sub-item", { complete: complete.value, active: active.value }])
  }, [
    _createVNode(Tile, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", null, [
              _createVNode(_unref(FontAwesomeIcon), {
                class: _normalizeClass(iconConfiguration.value.class),
                icon: iconConfiguration.value.icon
              }, null, 8, ["class", "icon"]),
              _createTextVNode(" " + _toDisplayString(displayName.value) + " " + _toDisplayString(formattedTitle.value) + " ", 1),
              (
                _unref(subitem)?.applicantId &&
                _unref(subitem)?.applicantId > 0 &&
                _unref(subitem)?.processingCategoryId !== _unref(ProcessingCategoryIds).Income
              )
                ? (_openBlock(), _createBlock(Pill, {
                    key: 0,
                    color: "gray position-absolute",
                    text: applicantTypeDisplayName.value,
                    maxHeight: '34px'
                  }, null, 8, ["text"]))
                : _createCommentVNode("", true)
            ])
          ]),
          (_unref(subitem).stipulationText && !complete.value)
            ? (_openBlock(), _createBlock(StipulationText, {
                key: 0,
                subitem: _unref(subitem),
                class: "hint"
              }, null, 8, ["subitem"]))
            : _createCommentVNode("", true),
          (_unref(subitem)?.stipulationStatusName)
            ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(__props.itemComplete && complete.value ? "Complete!" : ""), 1))
            : _createCommentVNode("", true)
        ]),
        (
          _unref(subitem)?.isExpectedDocument &&
          !complete.value &&
          !pending.value &&
          _unref(subitem)?.processingCategoryName !== 'Income'
        )
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(InlineUppyUploader, {
                ref: `${_unref(subitem)?.applicantId}_2_${_unref(subitem)?.id}`,
                name: `${_unref(subitem)?.applicantId}_2_${_unref(subitem)?.id}`,
                applicant: applicant.value,
                applicationId: _unref(subitem)?.applicationId,
                documentTypeId: _unref(subitem).docTypeId ? _unref(subitem).docTypeId : 23,
                stipulationId: _unref(subitem)?.id,
                modelValue: files[`${_unref(subitem)?.applicantId}_2_${_unref(subitem)?.id}`],
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((files[`${_unref(subitem)?.applicantId}_2_${_unref(subitem)?.id}`]) = $event)),
                onUploadSuccess: _cache[1] || (_cache[1] = ($event: any) => (handleUpload(_unref(subitem))))
              }, null, 8, ["name", "applicant", "applicationId", "documentTypeId", "stipulationId", "modelValue"])
            ]))
          : _createCommentVNode("", true),
        (
          _unref(subitem)?.isExpectedDocument &&
          !complete.value &&
          !pending.value &&
          _unref(subitem)?.processingCategoryId === _unref(ProcessingCategoryIds).Income
        )
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _withDirectives(_createElementVNode("div", null, " Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation. ", 512), [
                [_vShow, _unref(subitem)?.stipulationTypeId === _unref(IncomeStipulationTypeIds).OtherIncome]
              ]),
              _createVNode(UppyUploader, {
                ref: `${_unref(subitem)?.applicantId}_1_${_unref(subitem)?.id}`,
                modelValue: files[`${_unref(subitem)?.applicantId}_1_${_unref(subitem)?.id}`],
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((files[`${_unref(subitem)?.applicantId}_1_${_unref(subitem)?.id}`]) = $event)),
                name: `${_unref(subitem)?.applicantId}_1_${_unref(subitem)?.id}`,
                applicant: applicant.value,
                "show-toggle": true,
                documentTypeId: _unref(subitem).docTypeId ? _unref(subitem).docTypeId : 23,
                stipulationId: _unref(subitem)?.id,
                label: _unref(subitem)?.stipTypeDescription,
                onUploadSuccess: _cache[3] || (_cache[3] = ($event: any) => (handleUpload(_unref(subitem))))
              }, null, 8, ["modelValue", "name", "applicant", "documentTypeId", "stipulationId", "label"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_6, [
          (
            _unref(subitem)?.stipulationTypeId === _unref(EsignStipulationTypeIds).ExecuteLegalDocPackage ||
            _unref(subitem)?.stipulationTypeId === _unref(EsignStipulationTypeIds).ConfirmationEsign
          )
            ? (_openBlock(), _createBlock(ESign, {
                key: 0,
                onEsignClicked: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('esignClicked')))
              }))
            : _createCommentVNode("", true),
          (
            _unref(subitem)?.stipulationTypeId === _unref(CreditStipulationTypeIds).ADDMismatch &&
            !complete.value &&
            !pending.value
          )
            ? (_openBlock(), _createBlock(AddressMismatch, {
                key: 1,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: handleStipSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId === _unref(MembershipStipulationTypeIds).EstablishMembership
          )
            ? (_openBlock(), _createBlock(Membership, {
                key: 2,
                stip: _unref(subitem),
                applicant: _unref(assignedApplicant),
                onSuccess: handleStipSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId === _unref(IdentityStipulationTypeIds).NameVerification
          )
            ? (_openBlock(), _createBlock(NameInput, {
                key: 3,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: handleStipSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId === _unref(IdentityStipulationTypeIds).AddressVerification
          )
            ? (_openBlock(), _createBlock(AddressInput, {
                key: 4,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: handleStipSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId === _unref(IdentityStipulationTypeIds).DateOfBirthVerification
          )
            ? (_openBlock(), _createBlock(DateOfBirthInput, {
                key: 5,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: handleStipSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId === _unref(IdentityStipulationTypeIds).SSNVerification
          )
            ? (_openBlock(), _createBlock(SocialSecurityNumberInput, {
                key: 6,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: handleStipSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId === _unref(PayoffVerificationStipulationTypeIds).ConfirmPayoffRequest
          )
            ? (_openBlock(), _createBlock(ConfirmPayoffRequestInput, {
                key: 7,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: handleStipSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId ===
              _unref(PayoffVerificationStipulationTypeIds).ResponsibilityForUnderPayment
          )
            ? (_openBlock(), _createBlock(RefiUnderpaymentConsent, {
                key: 8,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: handleUpdateConsentStipResponseSuccess
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId ===
              _unref(PayoffVerificationStipulationTypeIds).ServicerPayoffAddress
          )
            ? (_openBlock(), _createBlock(ServicerPayoffAddressInput, {
                key: 9,
                stip: _unref(subitem),
                onSuccess: handleStipSuccess
              }, null, 8, ["stip"]))
            : _createCommentVNode("", true),
          (
            _unref(subitem)?.stipulationTypeId === _unref(RegulatoryStipulationTypeIds).SelfCertificationDetails
          )
            ? (_openBlock(), _createBlock(SelfCertificationDetailsInput, {
                key: 10,
                stip: _unref(subitem),
                onSuccess: handleStipSuccess
              }, null, 8, ["stip"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId ===
              _unref(IncomeStipulationTypeIds).AutomatedIncomeVerificationConsent
          )
            ? (_openBlock(), _createBlock(AutomatedIncomeVerificationConsent, {
                key: 11,
                stip: _unref(subitem),
                applicant: applicant.value
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true),
          (
            !complete.value &&
            !pending.value &&
            _unref(subitem)?.stipulationTypeId === _unref(IncomeStipulationTypeIds).IncomeVerification
          )
            ? (_openBlock(), _createBlock(AutomatedIncomeVerification, {
                key: 12,
                stip: _unref(subitem),
                applicant: applicant.value,
                onSuccess: _cache[5] || (_cache[5] = ($event: any) => (handleUpload(_unref(subitem))))
              }, null, 8, ["stip", "applicant"]))
            : _createCommentVNode("", true)
        ]),
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "footer" }, null, -1))
      ]),
      _: 1
    })
  ], 2))
}
}

})